'use client';

/**
 * MapsConfigProvider
 *
 * Google Maps API key: layout'ta bir kez alınır (getMapsConfigFromLaravel), burada initial olarak verilir.
 * Verilmezse /api/maps/config client'ta tek sefer çekilir (CaptchaConfigProvider ile aynı mantık).
 * ProductTourDetail, ContactGoogleMap, TourRouteMap vb. useMapsConfig ile bu context'ten key alır.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const MAPS_CONFIG_URL = '/api/maps/config';

export interface MapsConfigContextValue {
  api_key: string | null;
  map_id: string | null;
  isLoading: boolean;
  error: string | null;
  /** Layout'tan veri geldiyse client'ta /api/maps/config isteği atılmaz. */
  fromLayout: boolean;
  refetch: () => Promise<void>;
}

const MapsConfigContext = createContext<MapsConfigContextValue | undefined>(undefined);

export interface MapsConfigProviderProps {
  children: React.ReactNode;
  /** Layout'ta getMapsConfigFromLaravel() ile alınan key; verilirse client isteği atılmaz. */
  initialApiKey?: string | null;
  initialMapId?: string | null;
}

export function MapsConfigProvider({ children, initialApiKey, initialMapId }: MapsConfigProviderProps) {
  const hasInitial = initialApiKey !== undefined;
  /** Layout null geçtiyse client'ta bir kez dene (cache/key sonradan girilmiş olabilir) */
  const shouldRefetchWhenNull = hasInitial && (initialApiKey == null || initialApiKey === '');
  const [api_key, setApiKey] = useState<string | null>(hasInitial && !shouldRefetchWhenNull ? (initialApiKey ?? null) : null);
  const [map_id, setMapId] = useState<string | null>(
    initialMapId !== undefined ? (initialMapId?.trim() ? initialMapId.trim() : null) : null,
  );
  const [isLoading, setIsLoading] = useState(!hasInitial || shouldRefetchWhenNull);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const pendingRef = useRef<Promise<void> | null>(null);

  const fetchConfig = useCallback(async () => {
    if (typeof window === 'undefined') return;

    const doFetch = async () => {
      try {
        const res = await fetch(MAPS_CONFIG_URL, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!res.ok) throw new Error(`Maps config failed: ${res.status}`);
        const json = await res.json();
        const data = json?.data ?? json;
        const key = typeof data?.api_key === 'string' && data.api_key ? data.api_key : null;
        const mid =
          typeof data?.map_id === 'string' && data.map_id.trim() ? data.map_id.trim() : null;
        setApiKey(key);
        setMapId(mid);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Maps config alınamadı');
        setApiKey(null);
        setMapId(null);
      } finally {
        setIsLoading(false);
        pendingRef.current = null;
      }
    };

    if (pendingRef.current) {
      await pendingRef.current;
      return;
    }
    pendingRef.current = doFetch();
    await pendingRef.current;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (hasInitial && !shouldRefetchWhenNull) {
      setIsLoading(false);
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchConfig();
  }, [hasInitial, shouldRefetchWhenNull, fetchConfig]);

  const effectiveKey = shouldRefetchWhenNull ? api_key : (hasInitial ? (initialApiKey ?? null) : api_key);
  const value: MapsConfigContextValue = {
    api_key: effectiveKey,
    map_id: shouldRefetchWhenNull || !hasInitial ? map_id : (initialMapId?.trim() ? initialMapId.trim() : null),
    isLoading: shouldRefetchWhenNull ? isLoading : (hasInitial ? false : isLoading),
    error: shouldRefetchWhenNull ? error : (hasInitial ? null : error),
    fromLayout: hasInitial && !shouldRefetchWhenNull,
    refetch: hasInitial && !shouldRefetchWhenNull ? async () => {} : fetchConfig,
  };

  return (
    <MapsConfigContext.Provider value={value}>
      {children}
    </MapsConfigContext.Provider>
  );
}

export function useMapsConfigContext(): MapsConfigContextValue {
  const ctx = useContext(MapsConfigContext);
  if (ctx === undefined) {
    return {
      api_key: null,
      map_id: null,
      isLoading: false,
      error: null,
      fromLayout: false,
      refetch: async () => {},
    };
  }
  return ctx;
}
